import { Header } from "@/components/Header";
import { PremiumFooter } from "@/components/landing/PremiumFooter";
import { 
  Sparkles, Zap, Palette, Wand2, Image, Layers, 
  Maximize, Brush, Eraser, RefreshCw, Download, Shield,
  Clock, Globe, Smartphone, Cloud
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const features = [
  {
    icon: Sparkles,
    title: "Génération d'images IA",
    description: "Créez des images uniques à partir de simples descriptions textuelles grâce à notre modèle Floop.",
    color: "text-primary",
  },
  {
    icon: Wand2,
    title: "Retouche intelligente",
    description: "Transformez et améliorez vos photos avec des retouches automatiques de qualité professionnelle.",
    color: "text-accent",
  },
  {
    icon: Maximize,
    title: "Upscale 4K",
    description: "Augmentez la résolution de vos images jusqu'à 4K sans perte de qualité grâce à notre technologie avancée.",
    color: "text-highlight",
  },
  {
    icon: Eraser,
    title: "Suppression d'objets",
    description: "Effacez les éléments indésirables de vos photos en un clic. Notre IA reconstruit l'arrière-plan automatiquement.",
    color: "text-violet",
  },
  {
    icon: Layers,
    title: "Inpainting & Outpainting",
    description: "Étendez vos images au-delà de leurs bordures ou modifiez des zones spécifiques avec précision.",
    color: "text-primary",
  },
  {
    icon: Palette,
    title: "100+ Styles artistiques",
    description: "Appliquez des styles variés : réaliste, cartoon, anime, peinture, 3D, et bien plus encore.",
    color: "text-accent",
  },
  {
    icon: Brush,
    title: "Mode Beauté avancé",
    description: "Sublimez vos portraits avec des retouches naturelles : peau lissée, éclairage optimisé, yeux brillants.",
    color: "text-highlight",
  },
  {
    icon: RefreshCw,
    title: "Image vers Image",
    description: "Transformez une image existante en une nouvelle création tout en gardant sa structure de base.",
    color: "text-violet",
  },
  {
    icon: Image,
    title: "Floop Studio",
    description: "Un éditeur complet intégré pour peaufiner vos créations avec des outils professionnels.",
    color: "text-primary",
  },
  {
    icon: Download,
    title: "Export haute qualité",
    description: "Téléchargez vos créations en PNG, JPG ou WebP dans la résolution de votre choix.",
    color: "text-accent",
  },
  {
    icon: Shield,
    title: "Contenu sécurisé",
    description: "Notre filtre de sécurité intégré garantit des générations respectueuses et appropriées.",
    color: "text-highlight",
  },
  {
    icon: Clock,
    title: "Génération rapide",
    description: "Obtenez vos images en quelques secondes grâce à notre infrastructure optimisée.",
    color: "text-violet",
  },
];

const upcomingFeatures = [
  { icon: Globe, title: "API publique", description: "Intégrez Floop dans vos applications" },
  { icon: Smartphone, title: "Application mobile", description: "Créez où que vous soyez" },
  { icon: Cloud, title: "Stockage cloud", description: "Sauvegardez vos créations dans le cloud" },
];

const Features = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <section className="pt-24 pb-16 text-center">
        <div className="container mx-auto px-6">
          <span className="inline-block text-sm font-medium text-primary mb-4">
            Fonctionnalités
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 font-display">
            Tout ce dont vous avez besoin pour{" "}
            <span className="bg-gradient-floop bg-clip-text text-transparent">
              créer sans limites
            </span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Découvrez l'ensemble des outils et fonctionnalités qui font de Floop 
            la solution de génération d'images IA la plus complète du marché.
          </p>
          <Link to="/create">
            <Button size="lg" className="bg-gradient-floop hover:opacity-90 text-white rounded-full px-8">
              <Sparkles className="w-5 h-5 mr-2" />
              Essayer gratuitement
            </Button>
          </Link>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-card border border-border rounded-2xl p-6 hover:border-primary/50 transition-all duration-300 group"
              >
                <div className={`w-12 h-12 rounded-xl bg-secondary flex items-center justify-center mb-4 group-hover:scale-110 transition-transform ${feature.color}`}>
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">{feature.title}</h3>
                <p className="text-muted-foreground text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Features */}
      <section className="py-16 bg-secondary/30">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block text-sm font-medium text-accent mb-4">
              Bientôt disponible
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground font-display">
              Et ce n'est que le début
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {upcomingFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-card/50 border border-dashed border-border rounded-2xl p-6 text-center"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">{feature.title}</h3>
                <p className="text-muted-foreground text-sm">{feature.description}</p>
                <span className="inline-block mt-3 text-xs text-highlight font-medium">Coming Soon</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 font-display">
            Prêt à explorer toutes ces fonctionnalités ?
          </h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            Commencez gratuitement avec 10 crédits offerts. Aucune carte bancaire requise.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/create">
              <Button size="lg" className="bg-gradient-floop hover:opacity-90 text-white rounded-full px-8">
                Commencer gratuitement
              </Button>
            </Link>
            <Link to="/pricing">
              <Button size="lg" variant="outline" className="rounded-full px-8">
                Voir les tarifs
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <PremiumFooter />
    </div>
  );
};

export default Features;
